<script>
import {required, email} from "vuelidate/lib/validators";
import LoginService from "@/services/LoginService";
import UserProfileService from "@/services/UserProfileService";

/**
 * Login component
 */
export default {
  props: {
    showpasswordparent: {
      type: Boolean
    }
  },
  data() {
    return {
      loginService: new LoginService(this.uiErrorHandler, this.uiErrorHandlerClear),
      userProfileService: new UserProfileService,

      email: "",
      password: "",
      passwordFieldType: "password",
      submitted: false,
      tryingToLogIn: false,
      showPassword: false,
      showPasswordText: false,
      forgotPassword: false,
      alertUser: false,
      alertText: "",
      generalError: false,
      googlePlus: false,
      errorMsg: "There is a problem with this account. Please email support at techsupport@leveragepoint.com"
    };
  },

  validations () {
    if (this.googlePlus) {
      return {
        email: {
          required,
        },
        password: {
          required,
        },
      }
    }
    else if (this.forgotPassword) {
      return {
        email: {
          required,
          email,
        }
      }
    }
    else {
      return {
        email: {
          required,
          email,
        },
        password: {
          required,
        },
      }
    }
  },

  computed: {
    notification() {
      return this.$store ? this.$store.state.notification : null;
    },
    notificationAutoCloseDuration() {
      return this.$store && this.$store.state.notification ? 5 : 0;
    },
  },

  watch: {
    showPassword(newVal) {
      if (newVal === true) {
        this.$emit('showpasswordparent', !this.showpasswordparent)
      }
    }
  },

  mounted() {
    this.showPassword = this.userProfileService.isLeveragePointLogin();

    this.$drift.show();

    this.$refs.email.$el.focus()

    if(window.location.href.includes("errorCode")) {
      this.addAlert(this.errorMsg)
    }

    document.body.classList.add("authentication-bg");
    this._keyListener = function(e) {
      if (e.key === "g" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault(); // present "Save Page" from getting triggered.
        this.hiddenGem()
      }
    };

    document.addEventListener('keydown', this._keyListener.bind(this));

    if(window.location.href.includes("kylerocks")) {
      this.googlePlus = true
    }
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this._keyListener);
  },

  methods: {
    uiErrorHandler() {
      this.generalError = true;
    },

    uiErrorHandlerClear() {
      this.generalError = false;
    },

    async next() {
      if (this.email === "") {
        return;
      }

      this.$v.$touch();

      if (this.$v.email.$error) {
        return;
      }

      // Only need to validate things if this is the customer login
      if (!this.googlePlus) {
        await this.checkForSSO();
      }
    },

    async checkForSSO() {
      this.alertUser = false;

      const result = await this.loginService.checkForSSO(this.email);

      if (result) {
        window.location.href = result;
      } else {
        this.showPasswordField();
      }
    },

    showPasswordField() {
      this.showPassword = true;

      setTimeout(() => {
        if (this.$refs.password.$el) {
          this.$refs.password.$el.focus();
        }

      }, 200);
    },

    async resetPassword() {

      this.$v.$touch();
      if (this.$v.email.$invalid) {
        return;
      }

      const resultMessage = await this.loginService.resetPassword(this.email);
      this.addAlert(resultMessage);
      this.forgotPassword = false;
      this.showPasswordField();
    },

    async tryToLogIn() {
      this.alertUser = false;
      this.alertText = ""
      this.submitted = true;

      this.$v.$touch();

      // stop here if form is invalid
      if (this.$v.email.$invalid  || this.$v.password.$invalid) {
        return;
      }

      await this.loginService.login(this.email, this.password);
      if (this.loginService.isLoginSuccess()) {
        this.loginSuccessRedirect();
      }
      else {
        this.addAlert(this.loginService.getLoginErrorMessage());
      }
    },

    async googleSignIn() {
      const redirect = await this.loginService.googleSignIn();
      window.location.replace(redirect);

    },

    loginSuccessRedirect() {
      window.location.replace(this.loginService.getLoginRedirect())
    },

    addAlert(msg) {
      this.alertUser = true
      this.alertText = msg !== undefined ? msg : this.errorMsg
    },

    removeAlert() {
      this.alertText = ""
      this.alertUser = false
    },

    switchVisibility() {
      this.passwordFieldType = this.passwordFieldType === "password" ? "text" : "password";
    },

    initiateForgotPassword() {
      this.removeAlert()
      this.forgotPassword = true
      this.showPassword = false
      this.googlePlus = false
    },

    restart() {
      this.removeAlert()
      this.forgotPassword = false
      this.showPassword = true
      this.googlePlus = false
    },

    hiddenGem() {
      this.removeAlert()
      this.googlePlus = true;
      this.forgotPassword = false;
    },
  },
};
</script>
<template>
  <div>
    <div class="p-2">
      <div
          v-if="notification && notification.message"
          :class="'alert ' + notification.type"
      >
        {{ notification.message }}
      </div>

      <div v-if="this.generalError" style="padding-bottom: 50px;">
        <span style="float:left;text-align:left;color:#3A57A6;font-size: 0.85em;">We've experienced an error logging you in.  Please try again later.</span>
      </div>

      <div v-if="this.alertUser" style="padding-bottom: 50px;">
        <span style="float:left;text-align:left;color:#3A57A6;font-size: 0.85em;">{{ alertText }}</span>
      </div>

      <div v-if="forgotPassword && !googlePlus">
        <span>Having trouble logging in?</span><br>
        <span>To reset your password, enter your LeveragePoint email.</span><br><br>
      </div>

      <b-form @submit.prevent="tryToLogIn" :showPassword="showPassword">
        <!-- regular email sign in -->
        <b-form-group
            id="input-group-1"
            class="mb-3"
            label="Email"
            label-for="email"
        >
          <b-form-input
              name="email"
              id="email"
              v-model="email"
              type="text"
              placeholder="Enter email"
              ref="email"
              :class="{ 'is-invalid': $v.email.$error }"
              @keyup.enter="next"
              @blur="next"

          ></b-form-input>
          <div
              v-if="$v.email.$error"
              class="invalid-feedback"
          >
            <span v-if="!$v.email.required"
            >Email is required.</span
            >
            <span v-if="!googlePlus && !$v.email.email"
            >Please enter valid email.</span
            >
          </div>

        </b-form-group>

        <!-- password field for either case -->
        <b-form-group id="input-group-2" class="mb-3" v-if="(showPassword || googlePlus) && !forgotPassword">
          <label for="password">Password</label>
          <div class="flexContainer">
            <b-form-input
                name="password"
                id="password"
                ref="password"
                v-model="password"
                :type="passwordFieldType"
                @keyup.enter="tryToLogIn"
                placeholder="Enter password"
                :class="{
                            'is-invalid': submitted && $v.password.$error,
                          }"
            >
            </b-form-input>
            <b-button
                class="password-btn"
                @click=switchVisibility
                variant="light"
            >
              <font-awesome-icon v-if="passwordFieldType === 'password'" icon="fa-solid fa-eye" />
              <font-awesome-icon v-else-if="passwordFieldType !== 'password'" icon="fa-solid fa-eye-slash" />
            </b-button>
          </div>
          <div
              v-if="submitted && !$v.password.required"
              class="invalid-feedback"
          >
            Password is required.
          </div>

        </b-form-group>

        <!-- determines what buttons to show -->
        <div class="mt-3 text-end">
          <b-button variant="primary" class="w-sm" @click="next()" v-if="!showPassword && !forgotPassword && !googlePlus">Next
          </b-button>
          <div v-else-if="forgotPassword">
            <b-button variant="primary" class="back-btn" @click="restart()">Back</b-button>
            <b-button variant="primary" class="w-sm" @click="resetPassword()">Continue</b-button>
          </div>
          <div v-else-if="showPassword || googlePlus">
            <b-link class="gwt-Anchor" v-if="!forgotPassword" @click="initiateForgotPassword()"
                    style="float:left;cursor:pointer;">Forgot Your Password?
            </b-link>
            <br><br>
            <div class="buttons">
              <div class="inner" v-if="googlePlus && !forgotPassword"><b-button variant="primary" class="w-sm" @click="googleSignIn()">Google Sign In</b-button></div>
              <b-button variant="primary" class="w-sm inner" @click="tryToLogIn()">Sign In</b-button>
            </div>
          </div>
        </div>
      </b-form>



    </div>

  </div>
</template>


<style>
.btn.w-sm.btn-primary, .btn.back-btn.btn-primary {
  background-color: #ec6822;
  border-color: transparent;
  border-radius: 8px;
  width: 130px;
  height: 40px;
}

.btn.w-sm.btn-primary:hover, .btn.back-btn.btn-primary:hover {
  background-color: #0a7c41;
  border-color: transparent;
  border-radius: 8px;
  width: 130px;
  height: 40px;
}

.p-2 {
  margin-top: 0;
  margin-bottom: 0;
}
.flexContainer {
  display: flex;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

#input-2 {
  flex: 1;
  border: none;
}

.btn-check:focus + .btn-light, .btn-light:focus {
  box-shadow: none;
}

.btn-check:focus + .btn, .btn:focus {
  box-shadow: none;
}

.back-btn {
  float: left;
}

.buttons {
  width:100%;
  text-align: right;
}

.inner {
  margin-left: 10px;
  display: inline-block;
}

.text-end {
  padding-bottom: 20px;
}

@media screen and (max-width: 499px) {
  .p-2 input {
    font-size: 12px;
  }

  .btn.w-sm.btn-primary, .btn.back-btn.btn-primary {
    width: 80px;
    height: 30px;
    font-size: 10px;
  }

  .btn.w-sm.btn-primary:hover, .btn.back-btn.btn-primary:hover {
    width: 80px;
    height: 30px;
    font-size: 10px;
  }
}
</style>
